import buffer from "@turf/buffer";
import centroid from "@turf/centroid";
import length from "@turf/length";
import area from "@turf/area";
import intersect from "@turf/intersect";
import { featureCollection } from "@turf/helpers";
import type { Feature, Geometry, Polygon, MultiPolygon } from "geojson";

export const TURF_ENGINE = "turf.js" as const;
export const WEBR_ENGINE_LABEL = "webr-sf" as const;

/** Default sample: Plaza Bolívar, Caracas. */
export const DEFAULT_POINT: Geometry = {
  type: "Point",
  coordinates: [-66.8792, 10.5061],
};

export const DEFAULT_DISTANCE_METERS = 500;

/** Small polygon around Plaza Bolívar (~600 m). */
export const DEFAULT_POLYGON: Geometry = {
  type: "Polygon",
  coordinates: [
    [
      [-66.8832, 10.5031],
      [-66.8752, 10.5031],
      [-66.8752, 10.5091],
      [-66.8832, 10.5091],
      [-66.8832, 10.5031],
    ],
  ],
};

export const DEFAULT_LINE: Geometry = {
  type: "LineString",
  coordinates: [
    [-66.8832, 10.5061],
    [-66.8792, 10.5061],
    [-66.8752, 10.5101],
  ],
};

export const DEFAULT_INTERSECT_A: Geometry = {
  type: "Polygon",
  coordinates: [
    [
      [-66.884, 10.504],
      [-66.878, 10.504],
      [-66.878, 10.509],
      [-66.884, 10.509],
      [-66.884, 10.504],
    ],
  ],
};

export const DEFAULT_INTERSECT_B: Geometry = {
  type: "Polygon",
  coordinates: [
    [
      [-66.881, 10.505],
      [-66.875, 10.505],
      [-66.875, 10.510],
      [-66.881, 10.510],
      [-66.881, 10.505],
    ],
  ],
};

export const DEFAULT_GEOMETRY = DEFAULT_POINT;

/** Future Composer MCP App (schema → form). Not registered yet. */
export const COMPOSER_RESOURCE_URI = "ui://ansgis/composer";

export type GeoToolName =
  | "geo.buffer"
  | "geo.centroid"
  | "geo.measure"
  | "geo.intersect";

export type GeoComputeResult = {
  engine: typeof TURF_ENGINE | typeof WEBR_ENGINE_LABEL;
  feature?: Feature;
  extras?: Feature[];
  distanceMeters?: number;
  lengthMeters?: number;
  areaSqMeters?: number;
};

function asFeature(geometry: Geometry, properties: Record<string, unknown> = {}): Feature {
  return { type: "Feature", properties, geometry };
}

function assertGeometry(geometry: unknown, label = "geometría"): Geometry {
  if (!geometry || typeof geometry !== "object" || !("type" in geometry)) {
    throw new Error(`Hace falta una ${label} GeoJSON válida.`);
  }
  return geometry as Geometry;
}

export function computeGeoBuffer(
  geometry: Geometry,
  distanceMeters: number,
): GeoComputeResult {
  if (!Number.isFinite(distanceMeters) || distanceMeters <= 0) {
    throw new Error("La distancia debe ser un número positivo de metros.");
  }
  const input = asFeature(assertGeometry(geometry));
  const feature = buffer(input, distanceMeters, { units: "meters" });
  if (!feature) {
    throw new Error("Turf.js no pudo calcular el buffer para esa geometría.");
  }
  feature.properties = {
    ...(feature.properties ?? {}),
    distanceMeters,
    engine: TURF_ENGINE,
  };
  return { feature, distanceMeters, engine: TURF_ENGINE };
}

export function computeGeoCentroid(geometry: Geometry): GeoComputeResult {
  const input = asFeature(assertGeometry(geometry));
  const feature = centroid(input, {
    properties: { engine: TURF_ENGINE },
  });
  return {
    feature,
    extras: [input],
    engine: TURF_ENGINE,
  };
}

export function computeGeoMeasure(geometry: Geometry): GeoComputeResult {
  const geom = assertGeometry(geometry);
  const input = asFeature(geom, { engine: TURF_ENGINE });
  let lengthMeters = 0;
  let areaSqMeters = 0;
  try {
    lengthMeters = length(input, { units: "meters" });
  } catch {
    lengthMeters = 0;
  }
  try {
    areaSqMeters = area(input);
  } catch {
    areaSqMeters = 0;
  }
  input.properties = {
    ...(input.properties ?? {}),
    lengthMeters,
    areaSqMeters,
    engine: TURF_ENGINE,
  };
  return { feature: input, lengthMeters, areaSqMeters, engine: TURF_ENGINE };
}

export function computeGeoIntersect(
  geometry: Geometry,
  other: Geometry,
): GeoComputeResult {
  const a = asFeature(assertGeometry(geometry, "geometría A"));
  const b = asFeature(assertGeometry(other, "geometría B"));
  const polyA = a as Feature<Polygon | MultiPolygon>;
  const polyB = b as Feature<Polygon | MultiPolygon>;
  const feature = intersect(featureCollection([polyA, polyB]), {
    properties: { engine: TURF_ENGINE },
  });
  if (!feature) {
    throw new Error("Las geometrías no se intersectan.");
  }
  return {
    feature,
    extras: [a, b],
    engine: TURF_ENGINE,
  };
}

export function computeRemoteTool(
  name: GeoToolName,
  args: Record<string, unknown>,
): GeoComputeResult {
  switch (name) {
    case "geo.buffer":
      return computeGeoBuffer(
        args.geometry as Geometry,
        Number(args.distanceMeters),
      );
    case "geo.centroid":
      return computeGeoCentroid(args.geometry as Geometry);
    case "geo.measure":
      return computeGeoMeasure(args.geometry as Geometry);
    case "geo.intersect":
      return computeGeoIntersect(
        args.geometry as Geometry,
        args.other as Geometry,
      );
    default:
      throw new Error(`Herramienta desconocida: ${name}`);
  }
}
